import { useMutation } from '@tanstack/react-query';
import { DatePicker, Form, Space, TableProps } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { CalculationSetupApis } from 'apis/CalculationSetupApis';
import { GroupRoleApis } from 'apis/GroupRoleApis';
import { PlantApi } from 'apis/PlantApis';
import { EditIcon } from 'assets/svg';
import { ButtonAction, InputForm, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import TableComponent from 'components/Table/TableComponent';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { groupRewardOptions } from 'utils/constants/constant';
import { formatDate } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { HocChangePagination } from 'utils/HocChangePagination';
interface IProps extends React.PropsWithChildren<TableProps> {
  data: any;
  onSave: () => void;
  onCancel: () => void;
  onEdit: (data: any) => void;
  indexHandle: number;
  // setIndexHandle: (value: number) => void;
  isAdd: boolean;
  form: any;
  refetch: (isExport?: boolean, headerRow?: any) => void;
}

const statusOptions: any[] = [
  { label: 'Hoạt động', value: true },
  { label: 'Không hoạt động', value: false },
];

const { SaveTwoTone, CloseCircleTwoTone } = icons;
const TableRewardInfo = forwardRef((props: IProps, ref: any) => {
  const {
    data = {},
    loading,
    refetch,
    onSave,
    onCancel,
    onEdit,
    indexHandle,
    isAdd,
    form,
  } = props;

  const { user } = useUser();
  const [groupRoleManagementOptions, setGroupRoleManagementOptions] = useState<
    DefaultOptionType[]
  >([]);
  const [optionsPosition, setOptionsPosition] =
    useState<ILazySelectData>(initLazySelectData);
  const [plants, setPlants] = useState<DefaultOptionType[]>([]);

  const { mutate: getAllPlant, isPending: getAllPlantLoading } = useMutation({
    mutationFn: () => PlantApi.getAllPlantFilterHO(),
    onSuccess: (data: any) => {
      const items = data.data?.getPlant?.items || [];

      if (!items?.length) return setPlants([]);

      const plants = items.map((item: any) => ({
        label: item.id,
        value: item.id,
      }));

      setPlants(plants);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const {
    mutate: getComboboxGroupRoleManagement,
    isPending: isPendingGetComboboxGroupRoleManagement,
  } = useMutation({
    mutationFn: CalculationSetupApis.getComboboxGroupRoleManagement,
    onSuccess: (data) => {
      const rewardIds: string[] = [];
      const options = (data?.data?.getGroupRoleManagement?.items || [])
        .filter((item: any) => {
          if (rewardIds.includes(item.rewardId)) return false;
          rewardIds.push(item.rewardId);
          return true;
        })
        .map((item: any) => ({
          ...item,
          id: item.rewardId,
        }));

      setGroupRoleManagementOptions(options);
    },
  });

  const {
    mutate: getComboboxJobTitle,
    isPending: isPendingGetComboboxJobTitle,
  } = useMutation({
    mutationFn: GroupRoleApis.getComboboxJobTitle,
    onSuccess: (res) => {
      const data = res?.data?.getJobTitle?.items || [];

      if (data.length === 0) return;

      setOptionsPosition({
        options: data.map((item: any) => ({
          label: item.jobName,
          value: item.id,
        })),
        totalItems: res.data?.getJobTitle.pageInfo?.totalItems,
        hasNextPage: res.data?.getJobTitle.pageInfo?.hasNextPage,
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handlePositionSelectScrollChange = useCallback(
    (value: ILazySelectParams) => {
      getComboboxJobTitle({
        skip: 0,
        take: value.pageSize,
        jobName: value.value,
      });
    },
    [],
  );

  const handlePositionSelectChange = (value: any) => {
    if (value) {
      const jobTitle = optionsPosition.options.find(
        (item: any) => item.value === value,
      );

      form.setFieldsValue({
        jobName: jobTitle?.label,
      });
    } else {
      form.setFieldsValue({
        jobName: '',
      });
    }
  };

  const columns: ColumnsTypeCustom = useMemo(
    () => [
      {
        title: 'STT',
        width: 50,
        align: 'center',
        render: (_, __, index) =>
          data.pageInfo?.itemsPerPage * (data.pageInfo?.currentPage - 1) +
            index +
            1 || 1,
      },
      {
        title: 'Mã nhân viên',
        dataIndex: 'id',
        width: 130,
        render: (value, _, index) => {
          const isChange = isAdd && index === 0;

          if (isChange) {
            return (
              <Form.Item name="id" style={{ marginBottom: 0 }}>
                <InputForm placeholder="Mã nhân viên" />
              </Form.Item>
            );
          }

          return value || '-';
        },
      },
      {
        title: 'Tên nhân viên',
        dataIndex: 'name',
        width: 170,
        render: (value, _, index) => {
          const isChange = isAdd && index === 0;

          if (isChange) {
            return (
              <Form.Item name="name" style={{ marginBottom: 0 }}>
                <InputForm placeholder="Tên nhân viên" />
              </Form.Item>
            );
          }

          return value || '-';
        },
      },
      {
        title: 'Mã thưởng DMS',
        dataIndex: 'groupRoleManagementId',
        width: 130,
        render: (value, _, index) => {
          const isChange = (isAdd && index === 0) || index === indexHandle;

          if (isChange) {
            return (
              <Form.Item
                name="groupRoleManagementId"
                style={{ marginBottom: 0 }}
              >
                <SelectForm
                  fieldNames={{
                    label: 'rewardId',
                    value: 'rewardId',
                  }}
                  placeholder="Chọn mã thưởng DMS"
                  size="large"
                  // mode="multiple"
                  loading={isPendingGetComboboxGroupRoleManagement}
                  options={groupRoleManagementOptions}
                />
              </Form.Item>
            );
          }

          return value || '-';
        },
      },
      {
        title: 'Mã chức vụ',
        dataIndex: 'jobTitleCode',
        width: 130,
        render: (value, _, index) => {
          const isChange = (isAdd && index === 0) || index === indexHandle;

          if (isChange) {
            return (
              <Form.Item name="jobTitleCode" style={{ marginBottom: 0 }}>
                <LazySelectForm
                  fieldNames={{
                    label: 'value',
                  }}
                  customeSize={20}
                  data={optionsPosition}
                  loading={isPendingGetComboboxJobTitle}
                  placeholder="Chọn chức vụ"
                  onSelectScrollChange={handlePositionSelectScrollChange}
                  onChange={handlePositionSelectChange}
                />
              </Form.Item>
            );
          }

          return value || '-';
        },
      },
      {
        title: 'Chức vụ',
        dataIndex: 'jobName',
        width: 230,
        render: (value, _, index) => {
          const isChange = (isAdd && index === 0) || index === indexHandle;

          if (isChange) {
            return (
              <Form.Item name="jobName" style={{ marginBottom: 0 }}>
                <InputForm readOnly disabled placeholder="Chức vụ" />
              </Form.Item>
            );
          }

          return value || '-';
        },
      },
      // {
      //   title: 'Mã đơn vị nhỏ nhất',
      //   dataIndex: '',
      //   width: 180,
      // },
      {
        title: 'Nhóm',
        dataIndex: 'groupName',
        width: 130,
        // render: (value, _, index) => {
        //   const isChange = index === indexHandle;

        //   if (isChange) {
        //     return (
        //       <Form.Item name="groupName" style={{ marginBottom: 0 }}>
        //         <SelectForm
        //           options={groupRewardOptions}
        //           placeholder="Nhóm"
        //           size="large"
        //         />
        //       </Form.Item>
        //     );
        //   }

        //   return value || '-';
        // },
      },
      // {
      //   title: 'Đối tượng',
      //   dataIndex: '',
      //   width: 130,
      // },
      {
        title: 'Ngày ký HĐ',
        dataIndex: 'signDate',
        type: 'date',
        width: 150,
        render: (value, _, index) => {
          const isChange = (isAdd && index === 0) || index === indexHandle;

          if (isChange) {
            return (
              <Form.Item name="signDate" style={{ marginBottom: 0 }}>
                <DatePicker
                  size="large"
                  format="DD/MM/YYYY"
                  placeholder="Ngày ký HĐ"
                  maxDate={dayjs()}
                />
              </Form.Item>
            );
          }

          return (value && formatDate(value, 'DD/MM/YYYY')) || '-';
        },
      },
      {
        title: 'Ngày nghỉ việc',
        dataIndex: 'retireDate',
        width: 150,
        render: (value, _, index) => {
          const isChange = !isAdd && index === indexHandle;

          if (isChange) {
            return (
              <Form.Item name="retireDate" style={{ marginBottom: 0 }}>
                <DatePicker
                  size="large"
                  format="DD/MM/YYYY"
                  placeholder="Ngày nghỉ việc"
                />
              </Form.Item>
            );
          }

          return (value && formatDate(value, 'DD/MM/YYYY')) || '-';
        },
      },
      // {
      //   title: 'Trạng thái thưởng',
      //   dataIndex: '',
      //   width: 150,
      // },
      {
        title: 'Trạng thái',
        dataIndex: 'useYn',
        width: 130,
        // render: (value) => (value ? 'Hoạt động' : 'Không hoạt động'),
        render: (value, _, index) => {
          const isChange = !isAdd && index === indexHandle;

          if (isChange) {
            return (
              <Form.Item name="useYn" style={{ marginBottom: 0 }}>
                <SelectForm
                  size="large"
                  placeholder="Trạng thái"
                  options={statusOptions}
                />
              </Form.Item>
            );
          }

          if (isAdd) return '-';

          return value ? 'Hoạt động' : 'Không hoạt động';
        },
      },
      {
        title: 'Ghi chú',
        dataIndex: 'note',
        width: 230,
        render: (value, _, index) => {
          const isChange = (isAdd && index === 0) || index === indexHandle;

          if (isChange) {
            return (
              <Form.Item name="note" style={{ marginBottom: 0 }}>
                <InputForm size="large" placeholder="Ghi chú" maxLength={500} />
              </Form.Item>
            );
          }

          return value || '-';
        },
      },
      {
        title: 'Chi nhánh',
        dataIndex: 'plantId',
        width: 130,
        render: (value, _, index) => {
          const isChange = (isAdd && index === 0) || index === indexHandle;

          if (isChange) {
            return (
              <Form.Item name="plantId" style={{ marginBottom: 0 }}>
                <SelectForm
                  loading={getAllPlantLoading}
                  options={plants}
                  disabled={!(user?.isAdmin || user?.isHO)}
                  placeholder="Chọn chi nhánh"
                  size="large"
                />
              </Form.Item>
            );
          }

          return value || '-';
        },
      },
      // {
      //   title: 'Mã đơn vị cấp 1',
      //   dataIndex: '',
      //   width: 130,
      // },
      // {
      //   title: 'Đơn vị cấp 1',
      //   dataIndex: '',
      //   width: 130,
      // },
      // {
      //   title: 'Mã đơn vị cấp 2',
      //   dataIndex: '',
      //   width: 130,
      // },
      // {
      //   title: 'Đơn vị cấp 2',
      //   dataIndex: '',
      //   width: 130,
      // },
      // {
      //   title: 'Mã đơn vị cấp 3',
      //   dataIndex: '',
      //   width: 130,
      // },
      // {
      //   title: 'Đơn vị cấp 3',
      //   dataIndex: '',
      //   width: 130,
      // },
      // {
      //   title: 'Mã đơn vị cấp 4',
      //   dataIndex: '',
      //   width: 130,
      // },
      // {
      //   title: 'Đơn vị cấp 4',
      //   dataIndex: '',
      //   width: 130,
      // },
      {
        title: 'Người khai báo',
        width: 170,
        dataIndex: 'createName',
      },
      {
        title: 'Ngày khai báo',
        width: 170,
        type: 'date',
        dataIndex: 'createDate',
        render: (text) => formatDate(text),
      },
      {
        title: 'Người cập nhật',
        width: 130,
        dataIndex: 'modifyName',
      },
      {
        title: 'Ngày cập nhật',
        width: 170,
        type: 'date',
        dataIndex: 'modifyDate',
        render: (text) => formatDate(text),
      },
      {
        fixed: 'right',
        align: 'center',
        width: 70,
        render: (__, record, index) =>
          index === indexHandle ? (
            <Space size={'middle'} className="text-lg">
              <ButtonAction tooltip="Lưu" onClick={onSave}>
                <SaveTwoTone style={{ fontSize: 20 }} />
              </ButtonAction>
              <ButtonAction tooltip="Hủy" onClick={onCancel}>
                <CloseCircleTwoTone style={{ fontSize: 20 }} />
              </ButtonAction>
            </Space>
          ) : (
            <Space size={'middle'} className="text-lg ">
              <ButtonAction
                tooltip="Chỉnh sửa"
                onClick={() => onEdit(index)}
                disabled={!(user?.isAdmin || user?.isHO || user?.isNS)}
              >
                <EditIcon />
              </ButtonAction>
            </Space>
          ),
      },
    ],
    [
      data.pageInfo?.currentPage,
      data.pageInfo?.itemsPerPage,
      getAllPlantLoading,
      groupRoleManagementOptions,
      handlePositionSelectChange,
      handlePositionSelectScrollChange,
      indexHandle,
      isAdd,
      isPendingGetComboboxGroupRoleManagement,
      isPendingGetComboboxJobTitle,
      onCancel,
      onEdit,
      onSave,
      optionsPosition,
      plants,
      user?.isAdmin,
      user?.isHO,
    ],
  );

  useImperativeHandle(ref, () => ({
    exportExcel,
  }));

  const exportExcel = () => {
    refetch(true, columns);
  };

  useEffect(() => {
    getComboboxGroupRoleManagement({
      skip: 0,
      take: 100,
    });
  }, [getComboboxGroupRoleManagement]);

  useEffect(() => {
    getAllPlant();
  }, [getAllPlant]);

  return (
    <Content>
      <Form form={form}>
        <TableComponent
          scroll={{ x: 'max-content' }}
          columns={columns}
          // dataSource={[{}]}
          dataSource={data.items}
          rowKey={(record) => record?.id || 'ADD'}
          loading={loading}
          pagination={{
            disabled: isAdd || indexHandle !== -1,
            current: data.pageInfo?.currentPage,
            pageSize: data.pageInfo?.itemsPerPage + (isAdd ? 1 : 0),
            total: data.pageInfo?.totalItems,
            onChange: HocChangePagination(),
          }}
        />
      </Form>
    </Content>
  );
});

export default TableRewardInfo;
